import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { sortByNewest } from '../utils/sortByNewest';

export const fetchErrorLogs = createAsyncThunk('errorLogs/fetchMany', async (params) => {
    const response = await api.get('/error-logs', { params });
    return response.data;
});

export const fetchErrorLogUnreadCount = createAsyncThunk('errorLogs/fetchUnreadCount', async () => {
    const response = await api.get('/error-logs/unread-count');
    return response.data;
});

export const fetchErrorLogById = createAsyncThunk('errorLogs/fetchById', async (id) => {
    const response = await api.get(`/error-logs/${id}`);
    return response.data;
});

export const markErrorLogAsRead = createAsyncThunk('errorLogs/markAsRead', async (id) => {
    await api.put(`/error-logs/${id}/read`);
    return id;
});

export const markAllErrorLogsAsRead = createAsyncThunk('errorLogs/markAllAsRead', async () => {
    const response = await api.put('/error-logs/mark-all-read');
    return response.data;
});

const Slice = createSlice({
    name: 'errorLogs',
    initialState: {
        list: [],
        pagination: {
            currentPage: 1,
            totalPages: 0,
            totalCount: 0,
            limit: 50,
            hasMore: false
        },
        unreadCount: 0,
        status: 'idle',
        error: null,
        selectedLog: null,
        selectedStatus: 'idle',
        selectedError: null
    },
    reducers: {
        clearSelectedLog: (state) => {
            state.selectedLog = null;
            state.selectedStatus = 'idle';
            state.selectedError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch error logs
            .addCase(fetchErrorLogs.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchErrorLogs.fulfilled, (state, action) => {
                state.status = 'success';
                state.list = sortByNewest(action.payload.data || action.payload, 'lastOccurredAt');
                state.pagination = action.payload.pagination || state.pagination;
                state.error = null;
            })
            .addCase(fetchErrorLogs.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })

            // Fetch unread count
            .addCase(fetchErrorLogUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload.unreadCount || 0;
            })

            // Fetch single error log
            .addCase(fetchErrorLogById.pending, (state) => {
                state.selectedStatus = 'loading';
            })
            .addCase(fetchErrorLogById.fulfilled, (state, action) => {
                state.selectedStatus = 'success';
                state.selectedLog = action.payload;
                state.selectedError = null;
            })
            .addCase(fetchErrorLogById.rejected, (state, action) => {
                state.selectedStatus = 'failed';
                state.selectedError = action.error.message || 'An error occurred';
            })

            // Mark as read
            .addCase(markErrorLogAsRead.fulfilled, (state, action) => {
                const id = action.payload;
                const log = state.list.find(l => l._id === id);
                if (log) {
                    log.isRead = true;
                }
                if (state.selectedLog && state.selectedLog._id === id) {
                    state.selectedLog.isRead = true;
                }
                if (state.unreadCount > 0) {
                    state.unreadCount -= 1;
                }
            })

            // Mark all as read
            .addCase(markAllErrorLogsAsRead.fulfilled, (state) => {
                state.list.forEach(log => {
                    log.isRead = true;
                });
                if (state.selectedLog) {
                    state.selectedLog.isRead = true;
                }
                state.unreadCount = 0;
            });
    }
});

export const { clearSelectedLog } = Slice.actions;
export default Slice.reducer;
